import { useSnackbar } from 'notistack';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import MovimentationService, {
  MovementsMenuPermissions,
  ReviewsScreenMovement,
} from '@services/MovimentationService';

import { useAuth, useFilterParams } from '@hooks';
import { toastifyApiErrors } from '@utils';

import { MovementServiceTypeEnum } from './enums';
import {
  ACTIVE_CONTRACT_SERVICES,
  FIXED_SERVICES,
  NEW_CONTRACT_SERVICES,
} from './constants';
import { filterAllServices, mapAllServices } from './utils';

export function useMovementMenuOptions() {
  const { enqueueSnackbar } = useSnackbar();
  const { selectedPartnerCompany } = useAuth();
  const movementService = new MovimentationService();
  const { searchParam } = useFilterParams();

  const hasFilter = Boolean(searchParam?.search);

  const partnerCompany = selectedPartnerCompany?.partnerCompany ?? '';

  const isEnabled = Boolean(partnerCompany);

  const movementsMenuPermissionsState = useQuery<MovementsMenuPermissions[]>({
    queryKey: ['movementsMenuPermissionsState', partnerCompany],
    queryFn: () =>
      movementService.getMovementsMenuPermissions({
        partnerCompany,
      }),
    enabled: isEnabled,
    onError: error => {
      toastifyApiErrors(error, enqueueSnackbar);
    },
  });

  const reviewsScreenMovementState = useQuery<ReviewsScreenMovement>({
    queryKey: ['reviewsScreenMovementState'],
    queryFn: () => movementService.getReviewsScreenMovement(),
    cacheTime: 0,
    onError: error => {
      toastifyApiErrors(error, enqueueSnackbar);
    },
  });

  const refreshMovementsMenuPermissionsState = () => {
    movementsMenuPermissionsState.remove();
    reviewsScreenMovementState.remove();

    movementsMenuPermissionsState.refetch();
    reviewsScreenMovementState.refetch();
  };

  const allServices = useMemo(() => {
    const permissions = movementsMenuPermissionsState.data ?? [];

    const baseServices = [
      ...FIXED_SERVICES,
      ...NEW_CONTRACT_SERVICES,
      ...ACTIVE_CONTRACT_SERVICES,
    ];

    if (permissions?.length === 0) {
      return baseServices;
    }

    return mapAllServices(baseServices, permissions);
  }, [movementsMenuPermissionsState.data]);

  const filteredServices = useMemo(() => {
    if (!hasFilter) {
      return allServices;
    }

    const filter = searchParam?.search?.toLowerCase() ?? '';

    return filterAllServices(allServices, filter);
  }, [allServices, searchParam, hasFilter]);

  const fixedServices = useMemo(() => {
    return allServices.filter(
      service => service.type === MovementServiceTypeEnum.FIXED,
    );
  }, [allServices]);

  const newContractServices = useMemo(() => {
    return allServices.filter(
      service => service.type === MovementServiceTypeEnum.NEW_CONTRACT,
    );
  }, [allServices]);

  const activeContractServices = useMemo(() => {
    return allServices.filter(
      service => service.type === MovementServiceTypeEnum.ACTIVE_CONTRACT,
    );
  }, [allServices]);

  return {
    filteredServices,
    fixedServices,
    newContractServices,
    activeContractServices,
    movementsMenuPermissionsState,
    refreshMovementsMenuPermissionsState,
    reviewsScreenMovementState,
    hasFilter,
  };
}
